import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, CaretLeft, CaretRight, PencilSimple, Trash, FileText, Printer, X } from "@phosphor-icons/react";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, isSameMonth, isToday } from "date-fns";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { zar } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";

const STATUS_STYLE = {
  enquiry: "bg-muted text-muted-foreground",
  confirmed: "bg-[#9D5CFF]/15 text-[#C9A8FF]",
  completed: "bg-[#A9C97E]/15 text-[#A9C97E]",
  cancelled: "bg-[#F87171]/10 text-[#F87171]",
};

const EMPTY = { client_name: "", date: "", time: "", headcount: "", menu: "", beverages: "", gratuity: "", notes: "", status: "confirmed" };

export default function Functions() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "calendar";
  const [month, setMonth] = useState(() => format(new Date(), "yyyy-MM"));
  const [functions_, setFunctions] = useState([]);
  const [editing, setEditing] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const start = format(startOfMonth(new Date(month + "-01")), "yyyy-MM-dd");
  const end = format(endOfMonth(new Date(month + "-01")), "yyyy-MM-dd");
  const load = useCallback(() => api.get("/functions", { params: { start, end } })
    .then((r) => setFunctions(r.data)).catch(() => toast.error("Failed to load functions")), [start, end]);
  useEffect(() => { load(); }, [load]);

  if (!can("view_financials")) return <p className="text-muted-foreground" data-testid="fn-no-perm">You don't have permission to view functions.</p>;

  const save = async () => {
    if (!editing.client_name.trim() || !editing.date) return toast.error("Client name and date required");
    setSaving(true);
    try {
      const payload = { ...editing, headcount: editing.headcount === "" ? null : Number(editing.headcount) };
      delete payload._new;
      delete payload.invoice;
      if (editing._new) await api.post("/functions", payload);
      else await api.put(`/functions/${editing.id}`, payload);
      toast.success(editing._new ? "Function created — it's on the calendar" : "Function updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (fn) => {
    try {
      await api.delete(`/functions/${fn.id}`);
      toast.success("Function removed (linked invoice kept, unlinked)");
      load();
    } catch (e) {
      toast.error(apiError(e, "Delete failed"));
    }
  };

  const openSheet = async (fn) => {
    try {
      const { data } = await api.get(`/functions/${fn.id}/sheet`);
      setSheet(data);
    } catch (e) {
      toast.error(apiError(e, "Could not load function sheet"));
    }
  };

  return (
    <div className="space-y-6" data-testid="functions-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold">Functions &amp; Events</h1>
          <p className="text-muted-foreground mt-1">Functions land on the calendar automatically; revenue is recognized on the event date</p>
        </div>
        <Button onClick={() => setEditing({ ...EMPTY, _new: true })} data-testid="fn-new-btn" className="bg-primary hover:bg-[#8B5CF6] text-white">
          <Plus size={16} className="mr-1" /> New function
        </Button>
      </div>

      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList data-testid="fn-tabs">
          <TabsTrigger value="calendar" data-testid="fn-tab-calendar">Calendar</TabsTrigger>
          <TabsTrigger value="list" data-testid="fn-tab-list">Functions</TabsTrigger>
        </TabsList>

        <TabsContent value="calendar" className="mt-4">
          <Card className="border-border shadow-none">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between mb-3">
                <Button variant="ghost" size="sm" data-testid="cal-prev" onClick={() => setMonth(format(addMonths(new Date(month + "-01"), -1), "yyyy-MM"))}><CaretLeft size={16} /></Button>
                <p className="font-heading font-semibold" data-testid="cal-month-label">{format(new Date(month + "-01"), "MMMM yyyy")}</p>
                <Button variant="ghost" size="sm" data-testid="cal-next" onClick={() => setMonth(format(addMonths(new Date(month + "-01"), 1), "yyyy-MM"))}><CaretRight size={16} /></Button>
              </div>
              <MonthGrid month={month} functions={functions_} onOpen={(fn) => setEditing({ ...fn, headcount: fn.headcount ?? "" })} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="list" className="mt-4">
          <Card className="border-border shadow-none">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm min-w-[700px]" data-testid="fn-table">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="px-4 py-3 font-medium">Date</th>
                    <th className="px-4 py-3 font-medium">Client</th>
                    <th className="px-4 py-3 font-medium text-right">Headcount</th>
                    <th className="px-4 py-3 font-medium">Invoice</th>
                    <th className="px-4 py-3 font-medium text-right">Balance due</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {functions_.map((fn) => (
                    <tr key={fn.id} className="border-b last:border-0 hover:bg-accent/40" data-testid={`fn-row-${fn.id}`}>
                      <td className="px-4 py-2.5 num font-medium">{fn.date}{fn.time ? ` · ${fn.time}` : ""}</td>
                      <td className="px-4 py-2.5">{fn.client_name}</td>
                      <td className="px-4 py-2.5 num text-right">{fn.headcount ?? "—"}</td>
                      <td className="px-4 py-2.5 text-xs">{fn.invoice ? `${fn.invoice.invoice_no} (${fn.invoice.status})` : <span className="text-muted-foreground">none</span>}</td>
                      <td className="px-4 py-2.5 num text-right">{fn.invoice ? zar(fn.invoice.balance_due) : "—"}</td>
                      <td className="px-4 py-2.5"><Badge className={STATUS_STYLE[fn.status]} data-testid={`fn-status-${fn.id}`}>{fn.status}</Badge></td>
                      <td className="px-4 py-2.5">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost" title="Function sheet" data-testid={`fn-sheet-${fn.id}`} onClick={() => openSheet(fn)}><FileText size={15} /></Button>
                          {!fn.invoice && (
                            <Button size="sm" variant="ghost" title="Create invoice"
                              data-testid={`fn-invoice-${fn.id}`}
                              onClick={() => navigate(`/client-invoices?new=1&client=${encodeURIComponent(fn.client_name)}&fdate=${fn.date}&fid=${fn.id}`)}>
                              <Plus size={15} />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" data-testid={`fn-edit-${fn.id}`} onClick={() => setEditing({ ...fn, headcount: fn.headcount ?? "" })}><PencilSimple size={15} /></Button>
                          <Button size="sm" variant="ghost" className="text-destructive" data-testid={`fn-delete-${fn.id}`} onClick={() => remove(fn)}><Trash size={15} /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {functions_.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground" data-testid="fn-empty">No functions this month — create one, or set a function date on a client invoice.</td></tr>}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="fn-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing?._new ? "New function" : `Function — ${editing?.client_name}`}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Function details</DialogDescription>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5 col-span-2"><Label className="text-xs">Client *</Label><Input data-testid="fn-client" value={editing.client_name} onChange={(e) => setEditing({ ...editing, client_name: e.target.value })} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Date *</Label><Input type="date" data-testid="fn-date" value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Time</Label><Input type="time" data-testid="fn-time" value={editing.time} onChange={(e) => setEditing({ ...editing, time: e.target.value })} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Headcount</Label><Input type="number" min="0" data-testid="fn-headcount" value={editing.headcount} onChange={(e) => setEditing({ ...editing, headcount: e.target.value })} /></div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Status</Label>
                  <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                    <SelectTrigger data-testid="fn-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.keys(STATUS_STYLE).map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 col-span-2"><Label className="text-xs">Menu</Label><Textarea rows={2} data-testid="fn-menu" value={editing.menu} onChange={(e) => setEditing({ ...editing, menu: e.target.value })} /></div>
                <div className="space-y-1.5 col-span-2"><Label className="text-xs">Beverages</Label><Textarea rows={2} data-testid="fn-beverages" value={editing.beverages} onChange={(e) => setEditing({ ...editing, beverages: e.target.value })} /></div>
                <div className="space-y-1.5 col-span-2"><Label className="text-xs">Gratuity</Label><Input data-testid="fn-gratuity" value={editing.gratuity} onChange={(e) => setEditing({ ...editing, gratuity: e.target.value })} placeholder="e.g. 10% service charge included" /></div>
                <div className="space-y-1.5 col-span-2"><Label className="text-xs">Notes (allergies, celebrations, special requests)</Label><Textarea rows={2} data-testid="fn-notes" value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></div>
              </div>
              {editing.invoice && (
                <p className="text-xs text-muted-foreground border border-border rounded-md px-3 py-2">
                  Linked invoice {editing.invoice.invoice_no} — total {zar(editing.invoice.total)}, balance {zar(editing.invoice.balance_due)}.
                  Revenue is recognized on {editing.date} regardless of when the deposit landed.
                </p>
              )}
              <Button onClick={save} disabled={saving} data-testid="fn-save" className="w-full bg-primary hover:bg-[#8B5CF6] text-white">
                {saving ? "Saving…" : editing._new ? "Create function" : "Save changes"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <FunctionSheet sheet={sheet} onClose={() => setSheet(null)} />
    </div>
  );
}

function MonthGrid({ month, functions: fns, onOpen }) {
  const m = new Date(month + "-01");
  const first = startOfWeek(startOfMonth(m), { weekStartsOn: 1 });
  const last = endOfWeek(endOfMonth(m), { weekStartsOn: 1 });
  const byDate = useMemo(() => {
    const map = {};
    fns.forEach((f) => { (map[f.date] = map[f.date] || []).push(f); });
    return map;
  }, [fns]);
  const days = [];
  for (let d = first; d <= last; d = addDays(d, 1)) days.push(d);

  return (
    <div data-testid="fn-calendar">
      <div className="grid grid-cols-7 text-xs text-muted-foreground mb-1">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <span key={d} className="px-2 py-1">{d}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d) => {
          const iso = format(d, "yyyy-MM-dd");
          const dayFns = byDate[iso] || [];
          return (
            <div key={iso} data-testid={`cal-day-${iso}`}
              className={`min-h-[86px] rounded-md border p-1.5 ${isSameMonth(d, m) ? "border-border" : "border-transparent opacity-35"} ${isToday(d) ? "ring-1 ring-[#9D5CFF]" : ""}`}>
              <p className="text-[11px] text-muted-foreground mb-1">{format(d, "d")}</p>
              <div className="space-y-1">
                {dayFns.map((f) => (
                  <button key={f.id} onClick={() => onOpen(f)} data-testid={`cal-event-${f.id}`}
                    className={`w-full text-left rounded px-1.5 py-1 text-[11px] leading-tight border transition-colors ${f.status === "cancelled" ? "border-[#F87171]/30 text-muted-foreground line-through" : "border-[#9D5CFF]/40 bg-[#9D5CFF]/10 hover:bg-[#9D5CFF]/20"}`}>
                    <span className="font-medium block truncate">{f.client_name}</span>
                    <span className="text-[10px] text-muted-foreground">{[f.time, f.headcount && `${f.headcount} pax`].filter(Boolean).join(" · ")}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FunctionSheet({ sheet, onClose }) {
  if (!sheet) return null;
  const fn = sheet.function;
  const inv = fn.invoice;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="fn-sheet-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center justify-between">
            <span>Function Sheet — {fn.client_name}</span>
            <Button size="sm" variant="outline" data-testid="fn-sheet-print" onClick={() => window.print()}><Printer size={14} className="mr-1" /> Print</Button>
          </DialogTitle>
          <DialogDescription className="sr-only">Printable function sheet</DialogDescription>
        </DialogHeader>
        <div className="bg-white text-[#1A1A1A] rounded-lg p-6 text-sm space-y-4" data-testid="fn-sheet">
          <div className="flex justify-between items-start border-b-2 border-[#4A1D96] pb-3">
            <div>
              <p className="font-black text-xl text-[#4A1D96]">FUNCTION SHEET</p>
              <p className="text-[12px] text-[#666]">{sheet.venue_name}</p>
            </div>
            <div className="text-right">
              <p className="font-bold text-lg" data-testid="fn-sheet-date">{fn.date}{fn.time ? ` · ${fn.time}` : ""}</p>
              <p className="text-[12px] text-[#666]">{fn.status}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-x-8 gap-y-2">
            <div><p className="text-[10px] uppercase tracking-wide text-[#4A1D96] font-bold">Client</p><p className="font-semibold">{fn.client_name}</p></div>
            <div><p className="text-[10px] uppercase tracking-wide text-[#4A1D96] font-bold">Headcount</p><p className="font-semibold" data-testid="fn-sheet-headcount">{fn.headcount ?? "—"}</p></div>
          </div>
          <div className="grid grid-cols-2 gap-x-8 gap-y-3">
            <div><p className="text-[10px] uppercase tracking-wide text-[#4A1D96] font-bold">Menu</p><p className="whitespace-pre-line">{fn.menu || "—"}</p></div>
            <div><p className="text-[10px] uppercase tracking-wide text-[#4A1D96] font-bold">Beverages</p><p className="whitespace-pre-line">{fn.beverages || "—"}</p></div>
          </div>
          <div><p className="text-[10px] uppercase tracking-wide text-[#4A1D96] font-bold">Gratuity</p><p>{fn.gratuity || "—"}</p></div>
          <div><p className="text-[10px] uppercase tracking-wide text-[#4A1D96] font-bold">Notes / allergies / special requests</p><p className="whitespace-pre-line">{fn.notes || "—"}</p></div>
          <div className="border-t pt-3 space-y-1.5">
            <p className="text-[10px] uppercase tracking-wide text-[#4A1D96] font-bold">Financials {inv && `· invoice ${inv.invoice_no}`}</p>
            {inv ? (
              <>
                <div className="flex justify-between"><span>Total to be paid (VAT incl.)</span><span className="font-semibold" data-testid="fn-sheet-total">{zar(inv.total)}</span></div>
                <div className="flex justify-between"><span>Deposit required</span><span data-testid="fn-sheet-deposit">{zar(inv.deposit_required || 0)}</span></div>
                <div className="flex justify-between"><span>Deposit paid</span><span data-testid="fn-sheet-paid">{zar(inv.amount_paid)}</span></div>
                <div className="flex justify-between font-bold bg-[#4A1D96] text-white px-2 py-1 rounded">
                  <span>TO SETTLE ON THE EVENING</span><span data-testid="fn-sheet-balance">{zar(inv.balance_due)}</span>
                </div>
              </>
            ) : (
              <p className="text-[#666] italic">No invoice linked yet — create one from the Functions list.</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
